import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import UndoIcon from '@mui/icons-material/Undo';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import { useFormContext } from 'react-hook-form';
import { eventService } from '../../../services/eventService';
import ActivatePhasedWizard from './ActivatePhasedWizard';
import type { EventFormData } from '../EventFormContainer';

interface TicketCatalogModeBannerProps {
  eventId?: string;
  mode: 'legacy' | 'batched' | 'phased';
  onModeChanged: () => void;
}

const TicketCatalogModeBanner: React.FC<TicketCatalogModeBannerProps> = ({
  eventId,
  mode,
  onModeChanged,
}) => {
  const { watch } = useFormContext<EventFormData>();
  const tickets = watch('tickets') || [];
  const [wizardOpen, setWizardOpen] = useState(false);
  const [confirmRevertOpen, setConfirmRevertOpen] = useState(false);
  const [confirmUpgradeOpen, setConfirmUpgradeOpen] = useState(false);
  const [upgradeConflicts, setUpgradeConflicts] = useState<string[]>([]);
  const [upgradeWarnings, setUpgradeWarnings] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [canRevert, setCanRevert] = useState(false);
  const [revertReason, setRevertReason] = useState<string | null>(null);
  const [canMigrateToPhased, setCanMigrateToPhased] = useState(true);
  const [migrateBlockedReason, setMigrateBlockedReason] = useState<string | null>(null);
  const [soldCount, setSoldCount] = useState(0);

  useEffect(() => {
    if (!eventId) return;
    if (mode === 'legacy' || mode === 'batched') {
      eventService.getMigrateToPhasedStatus(eventId).then((res) => {
        if (res.success && res.data) {
          setCanMigrateToPhased(res.data.canMigrate);
          setMigrateBlockedReason(res.data.reason || null);
          setSoldCount(res.data.soldCount ?? 0);
        }
      }).catch(() => undefined);
    }
    if (mode === 'legacy' || mode === 'batched') return;
    eventService.getTicketCatalogRevertStatus(eventId).then((res) => {
      if (res.success && res.data) {
        setCanRevert(res.data.canRevert);
        setRevertReason(res.data.reason || null);
      }
    }).catch(() => undefined);
  }, [eventId, mode]);

  if (!eventId) return null;

  const handleUpgradeClick = async () => {
    if (!eventId) return;
    setLoading(true);
    setError(null);
    setUpgradeWarnings([]);
    try {
      const preview = await eventService.previewUpgradeBatchedV1(eventId);
      const conflicts = preview.success && preview.data?.conflicts ? preview.data.conflicts : [];
      if (conflicts.length > 0) {
        setUpgradeConflicts(conflicts);
        setConfirmUpgradeOpen(true);
        return;
      }
      await handleUpgrade();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erro no preview');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async () => {
    if (!eventId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await eventService.upgradeBatchedV1(eventId);
      if (!res.success) throw new Error(res.message || 'Erro no upgrade');
      if (res.warnings?.length) setUpgradeWarnings(res.warnings);
      setConfirmUpgradeOpen(false);
      onModeChanged();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erro no upgrade');
    } finally {
      setLoading(false);
    }
  };

  const handleRevert = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await eventService.revertTicketCatalog(eventId);
      if (!res.success) throw new Error(res.message || 'Erro ao reverter');
      setConfirmRevertOpen(false);
      onModeChanged();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erro ao reverter');
    } finally {
      setLoading(false);
    }
  };

  if (mode === 'legacy') {
    return (
      <>
        <Alert
          severity={canMigrateToPhased ? 'info' : 'warning'}
          sx={{ mb: 2 }}
          action={
            canMigrateToPhased ? (
              <Button color="inherit" size="small" startIcon={<SwapHorizIcon />} onClick={() => setWizardOpen(true)}>
                Vender em lotes
              </Button>
            ) : undefined
          }
        >
          Formato clássico: cada ingresso tem um preço e período de venda. Ative lotes para vender em fases (Lote 1, Lote 2…) com vários tipos de ingresso em cada fase.
          {!canMigrateToPhased && (
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              {migrateBlockedReason ||
                `Não é possível migrar: ${soldCount} ingresso(s) já vendido(s).`}
            </Typography>
          )}
        </Alert>
        {canMigrateToPhased && (
          <ActivatePhasedWizard
            open={wizardOpen}
            eventId={eventId}
            tickets={tickets.map((t: { _id?: string; name?: string; description?: string; price?: number; quantity?: number }) => ({
              _id: t._id,
              name: t.name || 'Ingresso',
              description: t.description,
              price: t.price ?? 0,
              quantity: t.quantity,
            }))}
            onClose={() => setWizardOpen(false)}
            onSuccess={onModeChanged}
          />
        )}
      </>
    );
  }

  if (mode === 'batched') {
    return (
      <>
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            canMigrateToPhased ? (
              <Button color="inherit" size="small" startIcon={<UpgradeIcon />} onClick={handleUpgradeClick} disabled={loading}>
                Atualizar para lotes globais
              </Button>
            ) : undefined
          }
        >
          Modo antigo (lote por ingresso). Recomendamos atualizar para lotes globais do evento.
          {!canMigrateToPhased && (
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              {migrateBlockedReason ||
                `Não é possível migrar: ${soldCount} ingresso(s) já vendido(s).`}
            </Typography>
          )}
          {error && <Typography variant="caption" display="block" color="error">{error}</Typography>}
        </Alert>
        {upgradeWarnings.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Upgrade concluído com ajustes de datas:
            {upgradeWarnings.map((w) => (
              <Typography key={w} variant="caption" display="block">{w}</Typography>
            ))}
          </Alert>
        )}
        <Dialog open={confirmUpgradeOpen} onClose={() => !loading && setConfirmUpgradeOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Datas dessincronizadas entre ingressos</DialogTitle>
          <DialogContent>
            <Typography variant="body2" paragraph>
              Os lotes de cada tipo de ingresso tinham datas diferentes. Ao unificar em lotes globais, usaremos as datas do primeiro tipo encontrado em cada lote:
            </Typography>
            <Stack spacing={1}>
              {upgradeConflicts.map((c) => (
                <Typography key={c} variant="body2" color="text.secondary">• {c}</Typography>
              ))}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmUpgradeOpen(false)} disabled={loading}>Cancelar</Button>
            <Button variant="contained" onClick={handleUpgrade} disabled={loading}>Continuar mesmo assim</Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  return (
    <>
      <Alert
        severity="success"
        sx={{ mb: 2 }}
        action={
          canRevert ? (
            <Button color="inherit" size="small" startIcon={<UndoIcon />} onClick={() => setConfirmRevertOpen(true)} disabled={loading}>
              Voltar ao formato clássico
            </Button>
          ) : undefined
        }
      >
        Venda em lotes ativa — todos os tipos de ingresso mudam de lote juntos.
        {!canRevert && revertReason && (
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
            Reversão indisponível: {revertReason}
          </Typography>
        )}
        {error && <Typography variant="caption" color="error" display="block">{error}</Typography>}
      </Alert>
      <Dialog open={confirmRevertOpen} onClose={() => !loading && setConfirmRevertOpen(false)}>
        <DialogTitle>Voltar ao formato clássico?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Isso só funciona se houver exatamente um lote e a estrutura não foi alterada após a migração.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRevertOpen(false)} disabled={loading}>Cancelar</Button>
          <Button variant="contained" onClick={handleRevert} disabled={loading}>Confirmar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TicketCatalogModeBanner;
